import PropTypes from 'prop-types';
import React from 'react';
import bindAll from 'lodash.bindall';
import ConnectionModalComponent, {PHASES} from '../components/connection-modal/connection-modal.jsx';
import VM from 'scratch-vm';
import analytics from '../lib/analytics';
import extensionData from '../lib/libraries/extensions/index.jsx';
import {connect} from 'react-redux';
import {closeConnectionModal} from '../reducers/modals';

const XIAOZHI_EXTENSION_ID = 'xijiXiaozhi';

class ConnectionModal extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleScanning',
            'handleCancel',
            'handleConnected',
            'handleConnecting',
            'handleDisconnect',
            'handleError',
            'handleHelp',
            'handleXiaozhiConnect',
            'handleXiaozhiStatusChange'
        ]);
        const isXiaozhiCloud = props.extensionId === XIAOZHI_EXTENSION_ID;
        this.state = {
            extension: extensionData.find(ext => ext.extensionId === props.extensionId),
            phase: isXiaozhiCloud || props.vm.getPeripheralIsConnected(props.extensionId) ?
                PHASES.connected : PHASES.scanning,
            xiaozhiConnecting: false,
            xiaozhiError: null,
            xiaozhiStatus: this.getXiaozhiStatus()
        };
    }
    componentDidMount () {
        this.props.vm.on('PERIPHERAL_CONNECTED', this.handleConnected);
        this.props.vm.on('PERIPHERAL_REQUEST_ERROR', this.handleError);
        this.attachXiaozhiStatusButton();
    }
    componentWillUnmount () {
        this.props.vm.removeListener('PERIPHERAL_CONNECTED', this.handleConnected);
        this.props.vm.removeListener('PERIPHERAL_REQUEST_ERROR', this.handleError);
        this.detachXiaozhiStatusButton();
    }
    get isXiaozhiCloud () {
        return this.props.extensionId === XIAOZHI_EXTENSION_ID;
    }
    getXiaozhiStatusButton () {
        return this.props.vm.runtime && this.props.vm.runtime.xiaozhiStatusButton;
    }
    getXiaozhiStatus (status) {
        const statusButton = this.getXiaozhiStatusButton();
        return Object.assign({
            isConnected: false,
            deviceStatus: '未连接',
            deviceCode: null,
            cloudState: 'stopped',
            authorized: false
        }, statusButton ? {
            isConnected: statusButton.isConnected,
            deviceStatus: statusButton.deviceStatus,
            deviceCode: statusButton.deviceCode,
            cloudState: statusButton.cloudState,
            authorized: statusButton.authorized
        } : {}, status || {});
    }
    attachXiaozhiStatusButton () {
        if (!this.isXiaozhiCloud) return;
        const statusButton = this.getXiaozhiStatusButton();
        if (!statusButton || statusButton.onStatusChange === this.xiaozhiStatusChangeWrapper) return;
        this.previousXiaozhiStatusChange = statusButton.onStatusChange;
        this.xiaozhiStatusChangeWrapper = status => {
            if (this.previousXiaozhiStatusChange &&
                this.previousXiaozhiStatusChange !== this.xiaozhiStatusChangeWrapper) {
                this.previousXiaozhiStatusChange(status);
            }
            this.handleXiaozhiStatusChange(status);
        };
        statusButton.onStatusChange = this.xiaozhiStatusChangeWrapper;
        this.setState({xiaozhiStatus: this.getXiaozhiStatus()});
    }
    detachXiaozhiStatusButton () {
        const statusButton = this.getXiaozhiStatusButton();
        if (statusButton && statusButton.onStatusChange === this.xiaozhiStatusChangeWrapper) {
            statusButton.onStatusChange = this.previousXiaozhiStatusChange || null;
        }
        this.previousXiaozhiStatusChange = null;
        this.xiaozhiStatusChangeWrapper = null;
    }
    handleScanning () {
        this.setState({
            phase: PHASES.scanning
        });
    }
    handleConnecting (peripheralId) {
        if (this.isXiaozhiCloud) return;
        this.props.vm.connectPeripheral(this.props.extensionId, peripheralId);
        this.setState({
            phase: PHASES.connecting
        });
        analytics.event({
            category: 'extensions',
            action: 'connecting',
            label: this.props.extensionId
        });
    }
    handleDisconnect () {
        if (this.isXiaozhiCloud) {
            const statusButton = this.getXiaozhiStatusButton();
            if (statusButton && statusButton.disconnectDevice) {
                statusButton.disconnectDevice();
            }
            this.setState({
                xiaozhiConnecting: false,
                xiaozhiError: null,
                xiaozhiStatus: this.getXiaozhiStatus({
                    isConnected: false,
                    deviceStatus: '已断开',
                    cloudState: 'stopped',
                    authorized: false
                })
            });
            return;
        }
        try {
            this.props.vm.disconnectPeripheral(this.props.extensionId);
        } finally {
            this.props.onCancel();
        }
    }
    handleCancel () {
        try {
            if (this.isXiaozhiCloud) {
                if (this.state.xiaozhiConnecting) {
                    const statusButton = this.getXiaozhiStatusButton();
                    if (statusButton && statusButton.disconnectDevice) {
                        statusButton.disconnectDevice();
                    }
                }
                return;
            }
            // If we're not connected to a peripheral, close the websocket so we stop scanning.
            if (!this.props.vm.getPeripheralIsConnected(this.props.extensionId)) {
                this.props.vm.disconnectPeripheral(this.props.extensionId);
            }
        } finally {
            // Close the modal.
            this.props.onCancel();
        }
    }
    handleXiaozhiStatusChange (status) {
        this.setState({
            xiaozhiStatus: this.getXiaozhiStatus(status)
        });
    }
    handleXiaozhiConnect (deviceCode) {
        const code = String(deviceCode || '').trim();
        const statusButton = this.getXiaozhiStatusButton();
        if (!code) {
            this.setState({xiaozhiError: '请输入设备编码'});
            return;
        }
        if (!statusButton || !statusButton.connectToDeviceCode) {
            this.setState({xiaozhiError: '智盒连接服务未准备好'});
            return;
        }
        this.setState({
            xiaozhiConnecting: true,
            xiaozhiError: null,
            xiaozhiStatus: this.getXiaozhiStatus({
                deviceCode: code,
                deviceStatus: '连接中',
                cloudState: 'connecting',
                authorized: false,
                isConnected: false
            })
        });
        statusButton.connectToDeviceCode(code)
            .then(() => {
                this.setState({
                    xiaozhiConnecting: false,
                    xiaozhiError: null,
                    xiaozhiStatus: this.getXiaozhiStatus({
                        deviceCode: code,
                        isConnected: true,
                        authorized: true,
                        deviceStatus: '在线'
                    })
                });
                analytics.event({
                    category: 'extensions',
                    action: 'connected',
                    label: this.props.extensionId
                });
            })
            .catch(error => {
                this.setState({
                    xiaozhiConnecting: false,
                    xiaozhiError: error && error.message ? error.message : '连接失败',
                    xiaozhiStatus: this.getXiaozhiStatus({
                        deviceCode: code,
                        isConnected: false
                    })
                });
                analytics.event({
                    category: 'extensions',
                    action: 'connecting error',
                    label: this.props.extensionId
                });
            });
    }
    handleError () {
        // Assume errors that come in during scanning phase are the result of not
        // having scratch-link installed.
        if (this.state.phase === PHASES.scanning || this.state.phase === PHASES.unavailable) {
            this.setState({
                phase: PHASES.unavailable
            });
        } else {
            this.setState({
                phase: PHASES.error
            });
            analytics.event({
                category: 'extensions',
                action: 'connecting error',
                label: this.props.extensionId
            });
        }
    }
    handleConnected () {
        this.setState({
            phase: PHASES.connected
        });
        analytics.event({
            category: 'extensions',
            action: 'connected',
            label: this.props.extensionId
        });
    }
    handleHelp () {
        window.open(this.state.extension.helpLink, '_blank');
        analytics.event({
            category: 'extensions',
            action: 'help',
            label: this.props.extensionId
        });
    }
    render () {
        return (
            <ConnectionModalComponent
                connectingMessage={this.state.extension && this.state.extension.connectingMessage}
                connectionIconURL={this.state.extension && this.state.extension.connectionIconURL}
                connectionSmallIconURL={this.state.extension && this.state.extension.connectionSmallIconURL}
                connectionTipIconURL={this.state.extension && this.state.extension.connectionTipIconURL}
                extensionId={this.props.extensionId}
                isXiaozhiCloud={this.isXiaozhiCloud}
                name={this.state.extension && this.state.extension.name}
                phase={this.state.phase}
                title={this.props.extensionId}
                useAutoScan={this.state.extension && this.state.extension.useAutoScan}
                vm={this.props.vm}
                onCancel={this.handleCancel}
                onConnected={this.handleConnected}
                onConnecting={this.handleConnecting}
                onDisconnect={this.handleDisconnect}
                onHelp={this.state.extension && this.state.extension.helpLink ? this.handleHelp : null}
                onScanning={this.handleScanning}
                onXiaozhiConnect={this.handleXiaozhiConnect}
                xiaozhiConnecting={this.state.xiaozhiConnecting}
                xiaozhiError={this.state.xiaozhiError}
                xiaozhiStatus={this.state.xiaozhiStatus}
            />
        );
    }
}

ConnectionModal.propTypes = {
    extensionId: PropTypes.string.isRequired,
    onCancel: PropTypes.func.isRequired,
    vm: PropTypes.instanceOf(VM).isRequired
};

const mapStateToProps = state => ({
    extensionId: state.scratchGui.connectionModal.extensionId
});

const mapDispatchToProps = dispatch => ({
    onCancel: () => {
        dispatch(closeConnectionModal());
    }
});

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(ConnectionModal);
